import {
  array,
  boolean,
  enum_,
  exactOptional,
  literal,
  never,
  number,
  object,
  optional,
  pipe,
  string,
  transform,
  union,
  variant,
  type InferOutput,
  type InferInput,
} from "valibot";
import {
  QuestionSchema,
  DraftQuestionSchema,
  PassAllQuestionSchema,
  PointsQuestionSchema,
  QuestionAnswerSchema,
} from "./question.ts";
import { compoundKey } from "@supply25/database/key";

export type { Question, QuestionType } from "./question.ts";

export const SectionSchema = object({
  id: exactOptional(string()),
  name: string(),
  questions: array(QuestionSchema),
});

export const DraftSectionSchema = object({
  ...SectionSchema.entries,
  questions: array(DraftQuestionSchema),
});

export const SectionsSchema = array(SectionSchema);

export const DraftSectionsSchema = array(DraftSectionSchema);

export type SectionsSchemaType = InferOutput<typeof SectionsSchema>;

const outcomeTargetSchema = object({ type: string(), value: string() });

const noOperationFields = {
  op: exactOptional(never()),
  target: exactOptional(never()),
};

export const OutcomeOp = {
  END: "END",
  ASSIGN: "ASSIGN",
} as const;

const endOperationFields = {
  op: literal(OutcomeOp.END),
  target: exactOptional(never()),
};

const assignOperationFields = {
  op: literal(OutcomeOp.ASSIGN),
  target: outcomeTargetSchema,
};

const baseOutcomeEntries = {
  id: string(),
  title: string(),
  name: string(),
  content: exactOptional(
    union([
      string(),
      // @todo: Deprecated. This is just to support old assessments.
      array(
        object({
          heading: string(),
          body: union([string(), array(string())]),
        }),
      ),
    ]),
  ),
  // @todo: Deprecated, this isn't used
  body: exactOptional(string()),
};

const PassAllOutcome = {
  SUCCESSFUL: "SUCCESSFUL",
  UNSUCCESSFUL: "UNSUCCESSFUL",
} as const;

const infoOutcome = {
  ...baseOutcomeEntries,
  type: literal(PassAllOutcome.SUCCESSFUL),
};

export const InfoOutcomeSchema = union([
  object({ ...infoOutcome, ...noOperationFields }),
  object({ ...infoOutcome, ...endOperationFields }),
  object({ ...infoOutcome, ...assignOperationFields }),
]);

const passOutcome = { ...baseOutcomeEntries, type: enum_(PassAllOutcome) };

export const PassOutcomeSchema = union([
  object({ ...passOutcome, ...noOperationFields }),
  object({ ...passOutcome, ...endOperationFields }),
  object({ ...passOutcome, ...assignOperationFields }),
]);

const min = { minScore: number() };
const max = { maxScore: number() };
const scores = { ...min, ...max };

export const ScoreOutcomeSchema = union([
  object({ ...baseOutcomeEntries, ...scores, ...noOperationFields }),
  object({ ...baseOutcomeEntries, ...scores, ...endOperationFields }),
  object({ ...baseOutcomeEntries, ...scores, ...assignOperationFields }),
  object({ ...baseOutcomeEntries, ...min, ...noOperationFields }),
  object({ ...baseOutcomeEntries, ...min, ...endOperationFields }),
  object({ ...baseOutcomeEntries, ...min, ...assignOperationFields }),
  object({ ...baseOutcomeEntries, ...max, ...noOperationFields }),
  object({ ...baseOutcomeEntries, ...max, ...endOperationFields }),
  object({ ...baseOutcomeEntries, ...max, ...assignOperationFields }),
]);

export const AssessmentType = {
  BUYER: "BUYER",
  SUPPLIER: "SUPPLIER",
} as const;

export const EvaluationType = {
  SCORE: "SCORE",
  PASS_ALL: "PASS_ALL",
  INFORMATION_ONLY: "INFORMATION_ONLY",
} as const;

export type EvaluationType =
  (typeof EvaluationType)[keyof typeof EvaluationType];

export const WordLimit = {
  INTRODUCTION: 250,
  GUIDANCE: 250,
  OUTCOME: 100,
  DESCRIPTION: 250,
} as const;

export const countWords = (text: string) =>
  text.split(/\s+/).filter(Boolean).length;

export const noDefaultOutcomesSchema = variant("evaluationType", [
  object({
    createDefaultOutcomes: optional(literal(false)),
    evaluationType: literal(EvaluationType.INFORMATION_ONLY),
    outcomes: array(InfoOutcomeSchema),
  }),
  object({
    createDefaultOutcomes: optional(literal(false)),
    evaluationType: literal(EvaluationType.PASS_ALL),
    outcomes: array(PassOutcomeSchema),
  }),
  object({
    createDefaultOutcomes: optional(literal(false)),
    evaluationType: literal(EvaluationType.SCORE),
    outcomes: ScoreOutcomeSchema,
  }),
]);

export const OutcomesSchema = union([
  array(InfoOutcomeSchema),
  array(PassOutcomeSchema),
  array(ScoreOutcomeSchema),
]);

export const packageAssessmentKey = {
  pk: compoundKey("TENDERPACKAGE"),
  sk: compoundKey("TPCOLLECTION", "TPASSESSMENT"),
  tk: compoundKey("URLID"),
};

const collectionEntries = {
  name: string(),
  description: exactOptional(string()),
};

export const tpCollectionKey = {
  pk: compoundKey("TENDERPACKAGE"),
  sk: compoundKey("TPCOLLECTION"),
};

export const TpCollectionSchema = pipe(
  object({
    ...tpCollectionKey,
    ...collectionEntries,
    buyerOrganisationId: string(),
    buyerWorkspaceId: string(),
    type: literal("TPCOLLECTION"),
  }),
  transform(({ pk, sk, ...rest }) => ({
    ...rest,
    packageId: pk.TENDERPACKAGE,
    id: sk.TPCOLLECTION,
  })),
);

export type Collection = InferOutput<typeof TpCollectionSchema>;

const IntroductionSchema = union([
  string(),
  // @todo: Deprecated. This is just to support old assessments.
  array(
    object({
      heading: string(),
      content: array(union([string(), array(string())])),
    }),
  ),
]);

const configBaseEntries = {
  name: string(),
  sortOrder: exactOptional(number()),
  assessmentType: enum_(AssessmentType),
  introduction: exactOptional(IntroductionSchema),
};

const sectionBase = { id: string(), name: string() };

const InfoAssessmentConfigSchema = object({
  ...configBaseEntries,
  evaluationType: literal(EvaluationType.INFORMATION_ONLY),
  outcomes: array(InfoOutcomeSchema),
  sections: array(
    object({ ...sectionBase, questions: array(PassAllQuestionSchema) }),
  ),
});

const PassAllAssessmentConfigSchema = object({
  ...configBaseEntries,
  evaluationType: literal(EvaluationType.PASS_ALL),
  outcomes: array(PassOutcomeSchema),
  sections: array(
    object({ ...sectionBase, questions: array(PassAllQuestionSchema) }),
  ),
});

const ScoreAssessmentConfigSchema = object({
  ...configBaseEntries,
  evaluationType: literal(EvaluationType.SCORE),
  outcomes: array(ScoreOutcomeSchema),
  sections: array(
    object({ ...sectionBase, questions: array(PointsQuestionSchema) }),
  ),
});

// The assessment content itself, as stored on any copy of an assessment
// (published, package, reassessment) — validated per evaluation type.
export const AssessmentConfigSchema = variant("evaluationType", [
  InfoAssessmentConfigSchema,
  PassAllAssessmentConfigSchema,
  ScoreAssessmentConfigSchema,
]);

export type AssessmentConfig = InferOutput<typeof AssessmentConfigSchema>;

const packageEntries = {
  ...packageAssessmentKey,
  type: literal("TPASSESSMENT"),
  selected: exactOptional(boolean()),
  completed: exactOptional(boolean()),
  buyerOrganisationId: string(),
  buyerWorkspaceId: string(),
};

const InfoAssessmentSchema = object({
  ...InfoAssessmentConfigSchema.entries,
  ...packageEntries,
});

const PassAllAssessmentSchema = object({
  ...PassAllAssessmentConfigSchema.entries,
  ...packageEntries,
});

const ScoreAssessmentSchema = object({
  ...ScoreAssessmentConfigSchema.entries,
  ...packageEntries,
});

export const PackageAssessmentSchema = pipe(
  variant("evaluationType", [
    InfoAssessmentSchema,
    PassAllAssessmentSchema,
    ScoreAssessmentSchema,
  ]),
  transform(({ pk, sk, tk, ...rest }) => ({
    ...rest,
    packageId: pk.TENDERPACKAGE,
    collectionId: sk.TPCOLLECTION,
    assessmentId: sk.TPASSESSMENT,
    urlId: tk.URLID,
  })),
);

export type PackageAssessment = InferOutput<typeof PackageAssessmentSchema>;
export type InfoAssessment = Extract<
  PackageAssessment,
  { evaluationType: typeof EvaluationType.INFORMATION_ONLY }
>;
export type PassAllAssessment = Extract<
  PackageAssessment,
  { evaluationType: typeof EvaluationType.PASS_ALL }
>;
export type ScoreAssessment = Extract<
  PackageAssessment,
  { evaluationType: typeof EvaluationType.SCORE }
>;

// A draft assessment is not yet assigned to a tender package
export const draftAssessmentKey = {
  pk: compoundKey("ORGANISATION", "WORKSPACE"),
  sk: compoundKey("COLLECTION", "ASSESSMENT"),
  tk: compoundKey("URLID"),
};

export const draftCollectionKey = {
  pk: compoundKey("ORGANISATION", "WORKSPACE"),
  sk: compoundKey("COLLECTION"),
};

export const DraftCollectionSchema = pipe(
  object({ ...draftCollectionKey, ...collectionEntries }),
  transform(({ pk, sk, ...rest }) => ({
    ...rest,
    organisationId: pk.ORGANISATION,
    workspaceId: pk.WORKSPACE,
    id: sk.COLLECTION,
    type: literal("COLLECTION"),
  })),
);

const draftAssessmentEntries = {
  name: string(),
  sortOrder: exactOptional(number()),
  assessmentType: enum_(AssessmentType),
  introduction: exactOptional(IntroductionSchema),
  ...draftAssessmentKey,
  type: literal("ASSESSMENT"),
};

export const DraftAssessmentSchema = pipe(
  variant("evaluationType", [
    object({
      ...draftAssessmentEntries,
      evaluationType: literal(EvaluationType.INFORMATION_ONLY),
      outcomes: array(InfoOutcomeSchema),
      sections: array(
        object({ ...sectionBase, questions: array(PassAllQuestionSchema) }),
      ),
    }),
    object({
      ...draftAssessmentEntries,
      evaluationType: literal(EvaluationType.PASS_ALL),
      outcomes: array(PassOutcomeSchema),
      sections: array(
        object({ ...sectionBase, questions: array(PassAllQuestionSchema) }),
      ),
    }),
    object({
      ...draftAssessmentEntries,
      evaluationType: literal(EvaluationType.SCORE),
      outcomes: array(ScoreOutcomeSchema),
      sections: array(
        object({ ...sectionBase, questions: array(PointsQuestionSchema) }),
      ),
    }),
  ]),
  transform(({ pk, sk, tk, ...rest }) => ({
    ...rest,
    organisationId: pk.ORGANISATION,
    workspaceId: pk.WORKSPACE,
    collectionId: sk.COLLECTION,
    assessmentId: sk.ASSESSMENT,
    urlId: tk.URLID,
  })),
);

export type DraftAssessment = InferOutput<typeof DraftAssessmentSchema>;

// A published assessment lives in a published collection and, like a draft,
// is not yet assigned to a tender package. It has no owning org/workspace.
export const pubAssessmentKey = {
  pk: compoundKey("PUBCOLLECTION"),
  sk: compoundKey("PUBASSESSMENT"),
  tk: compoundKey("URLID"),
};

export const pubCollectionKey = {
  pk: compoundKey("PUBCOLLECTION"),
  sk: literal("PUBCOLLECTION#META"),
};

export const PubCollectionSchema = pipe(
  object({
    ...pubCollectionKey,
    ...collectionEntries,
    type: literal("PUBCOLLECTION"),
  }),
  transform(({ pk, sk, ...rest }) => ({
    ...rest,
    id: pk.PUBCOLLECTION,
  })),
);

const pubAssessmentEntries = {
  name: string(),
  sortOrder: exactOptional(number()),
  assessmentType: enum_(AssessmentType),
  introduction: exactOptional(IntroductionSchema),
  ...pubAssessmentKey,
  type: literal("PUBASSESSMENT"),
};

export const PubAssessmentSchema = pipe(
  variant("evaluationType", [
    object({
      ...pubAssessmentEntries,
      evaluationType: literal(EvaluationType.INFORMATION_ONLY),
      outcomes: array(InfoOutcomeSchema),
      sections: array(
        object({ ...sectionBase, questions: array(PassAllQuestionSchema) }),
      ),
    }),
    object({
      ...pubAssessmentEntries,
      evaluationType: literal(EvaluationType.PASS_ALL),
      outcomes: array(PassOutcomeSchema),
      sections: array(
        object({ ...sectionBase, questions: array(PassAllQuestionSchema) }),
      ),
    }),
    object({
      ...pubAssessmentEntries,
      evaluationType: literal(EvaluationType.SCORE),
      outcomes: array(ScoreOutcomeSchema),
      sections: array(
        object({ ...sectionBase, questions: array(PointsQuestionSchema) }),
      ),
    }),
  ]),
  transform(({ pk, sk, tk, ...rest }) => ({
    ...rest,
    collectionId: pk.PUBCOLLECTION,
    assessmentId: sk.PUBASSESSMENT,
    urlId: tk.URLID,
  })),
);

export type PubAssessment = InferOutput<typeof PubAssessmentSchema>;

const answerEntries = {
  lastUpdated: string(),
  createdDate: string(),

  type: literal("ANSWER"),
  attempt: number(),
  authorId: string(),
  title: string(),
  ...QuestionAnswerSchema.entries,
};

// The answer shape as stored in the DB
const AnswerDbSchema = object({
  ...answerEntries,
  pk: compoundKey("ORGANISATION", "WORKSPACE"),
  sk: compoundKey("URLID", "QUESTION"),
  tk: compoundKey("QUESTION"),
});

// The answer shape after parsing with Valibot
export const AnswerOutSchema = object({
  ...answerEntries,
  organisationId: string(),
  workspaceId: string(),
  assessmentId: string(),
  questionId: string(),
});

// Take the stored DB shape and parse out the keys so app code can use them
export const AnswerSchema = pipe(
  AnswerDbSchema,
  transform(
    ({ pk, sk, tk, ...rest }) =>
      ({
        ...rest,
        organisationId: pk.ORGANISATION,
        workspaceId: pk.WORKSPACE,
        assessmentId: sk.URLID,
        questionId: sk.QUESTION,
      }) satisfies InferInput<typeof AnswerOutSchema>,
  ),
);

// Take the parsed output shape and encode back to DB shape for storage
export const AnswerInSchema = pipe(
  AnswerOutSchema,
  transform(
    ({ organisationId, workspaceId, assessmentId, questionId, ...a }) =>
      ({
        ...a,
        pk: AnswerDbSchema.entries.pk.encode({
          ORGANISATION: organisationId,
          WORKSPACE: workspaceId,
        }),
        sk: AnswerDbSchema.entries.sk.encode({
          URLID: assessmentId,
          QUESTION: questionId,
        }),
        tk: AnswerDbSchema.entries.tk.encode({ QUESTION: questionId }),
      }) satisfies InferInput<typeof AnswerDbSchema>,
  ),
);

export type Answer = InferOutput<typeof AnswerSchema>;
export type AnswerIn = InferOutput<typeof AnswerInSchema>;
export const AnswersSchema = array(AnswerSchema);
export const AnswersInSchema = array(AnswerInSchema);
